import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxInvoicesOutgoing,
  type TaxInvoiceOutgoingListItem,
} from '@/api/taxInvoices';
import { goodsSales } from '@/api/goodsSales';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TaxInvoicesOutgoingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [selOrg, setSelOrg] = useState('');
  const [selSale, setSelSale] = useState('');
  const [selNumber, setSelNumber] = useState('');

  const list = useQuery({
    queryKey: ['tax-invoices-outgoing'],
    queryFn: () => taxInvoicesOutgoing.list({ limit: 200 }),
  });
  // UX β: org dropdown через usePermissionsByOrg, окремий orgs.list не потрібен
  const salesQ = useQuery({
    queryKey: ['goods-sales-posted'],
    queryFn: () => goodsSales.list({ posted: true, limit: 200 }),
    enabled: createOpen,
  });

  const create = useMutation({
    mutationFn: () => taxInvoicesOutgoing.createFromSale({
      organization_id: selOrg,
      base_document_type: 'doc.goods_sales',
      base_document_id: selSale,
      invoice_number: selNumber || undefined,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
      setCreateOpen(false);
      navigate(`/tax-invoices-outgoing/${doc.id}`);
    },
  });

  const cols = useMemo<ColumnDef<TaxInvoiceOutgoingListItem>[]>(() => [
    { key: 'invoice_number', header: '№ НН', width: 150, importBadge: true, sortValue: (r) => r.invoice_number ?? '', cell: (r) => r.invoice_number },
    {
      key: 'invoice_date', header: t('cd.date'), width: 130,
      sortValue: (r) => r.invoice_date ?? '',
      cell: (r) => r.invoice_date
        ? new Date(r.invoice_date).toLocaleDateString(i18n.language)
        : '',
    },
    { key: 'contractor_name', header: t('gs.contractor'), sortValue: (r) => r.contractor_name ?? '', cell: (r) => r.contractor_name },
    {
      key: 'total_amount', header: t('cd.amount'), width: 130, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt(r.total_amount),
    },
    {
      key: 'erpn_status', header: 'ЄРПН', width: 130,
      sortValue: (r) => r.erpn_status ?? '',
      cell: (r) => r.erpn_status === 'REGISTERED'
        ? <span style={{ color: '#15803d', fontWeight: 600 }}>{r.erpn_status}</span>
        : r.erpn_status,
    },
    {
      key: 'posted', header: t('cd.posted'), width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no'),
    },
  ], [t, i18n.language]);

  // UX β: фільтр org dropdown по tax.invoice.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('tax.invoice.manage');
  if (!selOrg && availableOrgs[0]) setSelOrg(availableOrgs[0].id);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Податкові накладні (вихідні)</h2>
        <button
          className="primary"
          onClick={() => setCreateOpen(true)}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати податкові накладні у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Створити з реалізації
        </button>
      </div>
      <DataTable<TaxInvoiceOutgoingListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/tax-invoices-outgoing/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="invoice_date"
        initialSortDir="desc"
      />

      <Drawer
        open={createOpen}
        title="Нова податкова накладна"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!selSale || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.create')}
            </button>
          </>
        }
      >
        <div className="field">
          <label>{t('gs.organization')}</label>
          <select value={selOrg} onChange={(e) => setSelOrg(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>Документ-підстава (реалізація)</label>
          <select value={selSale} onChange={(e) => setSelSale(e.target.value)}>
            <option value="">—</option>
            {salesQ.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.document_number} ({new Date(s.document_date).toLocaleDateString('uk-UA')}) — {fmt(s.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>№ НН (можна залишити порожнім — візьметься з реалізації)</label>
          <input value={selNumber} onChange={(e) => setSelNumber(e.target.value)} />
        </div>
      </Drawer>
    </>
  );
}
